import { useCallback, useMemo, useState } from 'react';
import type { PosLine, PosProduct } from './types';

/** productId → miqdor */
type Cart = Record<number, number>;
/** productId → sotuvchi qo'ygan narx */
type CustomPrices = Record<number, number>;

/**
 * Savat holati — miqdor, ombor chegarasi va qo'lda qo'yilgan narxlar.
 *
 * Narx qoidalari serverdagi `OrderService` bilan bir xil: ro'yxat narxidan
 * yuqori bo'lolmaydi va tan narxdan past tusha olmaydi. Bu yerdagi tekshiruv
 * — foydalanuvchini xatoni yuborgandan keyin emas, yozayotganda ogohlantirish
 * uchun; haqiqiy qaror baribir serverda.
 */
export function usePosCart(products: PosProduct[], canEditPrices: boolean) {
  const [cart, setCart] = useState<Cart>({});
  const [customPrices, setCustomPrices] = useState<CustomPrices>({});
  const [stockWarning, setStockWarning] = useState<{ id: number; message: string } | null>(null);

  const byId = useMemo(() => {
    const map = new Map<number, PosProduct>();
    products.forEach((p) => map.set(p.id, p));
    return map;
  }, [products]);

  const warnStock = useCallback((product: PosProduct) => {
    setStockWarning({
      id: product.id,
      message: `Omborda yetarli emas — mavjud: ${product.stock} ta`,
    });
    window.setTimeout(() => setStockWarning(null), 2500);
  }, []);

  const setQuantity = useCallback(
    (productId: number, quantity: number) => {
      const product = byId.get(productId);
      if (!product) return;

      if (quantity > product.stock) {
        warnStock(product);
        return;
      }
      setCart((prev) => {
        if (quantity <= 0) {
          const next = { ...prev };
          delete next[productId];
          return next;
        }
        return { ...prev, [productId]: quantity };
      });
    },
    [byId, warnStock],
  );

  const step = useCallback(
    (productId: number, delta: number) => {
      setQuantity(productId, (cart[productId] || 0) + delta);
    },
    [cart, setQuantity],
  );

  /**
   * Qo'lda narx qo'yish. Bo'sh qiymat — ro'yxat narxiga qaytish.
   * Qaytadi: xato matni (yo'q bo'lsa null).
   */
  const setPrice = useCallback(
    (productId: number, raw: string): string | null => {
      const product = byId.get(productId);
      if (!product) return null;

      const value = parseInt(raw.replace(/\s/g, ''), 10);
      if (!raw || Number.isNaN(value) || value <= 0) {
        setCustomPrices((prev) => {
          const next = { ...prev };
          delete next[productId];
          return next;
        });
        return null;
      }

      setCustomPrices((prev) => ({ ...prev, [productId]: value }));

      if (value > product.sell_price_uzs) {
        return `Asl narxdan yuqori bo'lmasin — ${product.sell_price_uzs.toLocaleString('uz-UZ')}`;
      }
      if (product.cost_uzs && value < product.cost_uzs) {
        return `Tan narxdan past bo'lmasin — ${product.cost_uzs.toLocaleString('uz-UZ')}`;
      }
      return null;
    },
    [byId],
  );

  const priceErrors = useMemo(() => {
    if (!canEditPrices) return {} as Record<number, string>;
    const errors: Record<number, string> = {};
    Object.entries(customPrices).forEach(([id, value]) => {
      const product = byId.get(+id);
      if (!product) return;
      if (value > product.sell_price_uzs) {
        errors[+id] = `Asl narxdan yuqori bo'lmasin — ${product.sell_price_uzs.toLocaleString('uz-UZ')}`;
      } else if (product.cost_uzs && value < product.cost_uzs) {
        errors[+id] = `Tan narxdan past bo'lmasin — ${product.cost_uzs.toLocaleString('uz-UZ')}`;
      }
    });
    return errors;
  }, [byId, canEditPrices, customPrices]);

  const lines: PosLine[] = useMemo(
    () =>
      Object.entries(cart)
        .map(([id, quantity]) => {
          const product = byId.get(+id);
          if (!product) return null;
          // Ruxsati yo'q bo'lsa qo'lda qo'yilgan narx e'tiborga olinmaydi —
          // server ham aynan shunday qiladi.
          const custom = canEditPrices ? customPrices[product.id] : undefined;
          const price = custom && custom > 0 ? custom : product.sell_price_uzs;
          return {
            product,
            quantity,
            price,
            subtotal: price * quantity,
            isDiscounted: price < product.sell_price_uzs,
          };
        })
        .filter((line): line is PosLine => line !== null),
    [byId, canEditPrices, cart, customPrices],
  );

  const count = useMemo(() => lines.reduce((sum, l) => sum + l.quantity, 0), [lines]);
  const subtotal = useMemo(() => lines.reduce((sum, l) => sum + l.subtotal, 0), [lines]);

  const reset = useCallback(() => {
    setCart({});
    setCustomPrices({});
    setStockWarning(null);
  }, []);

  return {
    cart,
    customPrices,
    lines,
    count,
    subtotal,
    priceErrors,
    hasPriceErrors: Object.keys(priceErrors).length > 0,
    stockWarning,
    step,
    setQuantity,
    setPrice,
    reset,
  };
}
